import React, { useState, useCallback, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Typography,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme, alpha } from '@mui/material/styles';
import { ReactComponent as DropdownArrow } from '../../assets/DropdownArrow.svg';

const hundredMiliSecond = 100;
const DropdownIcon = (_props) => <DropdownArrow {..._props} />;

const DropDown = memo(
  ({
    label,
    name,
    value,
    onChange,
    helperText,
    children,
    required = false,
    placeholder,
    labelSx,
    ...props
  }) => {
    const theme = useTheme();
    const [labelShrinkState, setLabelShrinkState] = useState();
    const [shrinkState, setShrinkState] = useState(false);
    const inputLabel = useRef();
    const selectRef = useRef();

    console.log(shrinkState, setLabelShrinkState);

    const handleOpen = useCallback(() => {
      setShrinkState(true);
    }, [setShrinkState]);

    const handleClose = useCallback(() => {
      setShrinkState(false);
      // this might break with mui version update.
      // changes are done for https://solytics.atlassian.net/browse/NNG-7585
      setTimeout(() => {
        selectRef?.current?.children[0]?.blur();
      }, [hundredMiliSecond]);
    }, [selectRef, setShrinkState, hundredMiliSecond]);

    return (
      <FormControl error={Boolean(helperText)} variant="outlined">
        <InputLabel
          id="select-dropdown-label"
          required={required}
          ref={inputLabel}
          shrink={true}
          sx={{
            transform: 'none',
            top: '-24px',
            ...labelSx,
          }}
        >
          {label}
        </InputLabel>
        <Select
          labelId="select-dropdown-label"
          name={name}
          displayEmpty
          value={value || ''}
          onChange={onChange}
          IconComponent={DropdownIcon}
          renderValue={
            !['', undefined].includes(value)
              ? undefined
              : () => (
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: '400',
                      color: theme.palette.text.dropdown,
                    }}
                  >
                    {placeholder}
                  </Typography>
                )
          }
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            style: {
              marginTop: '0.1rem',
            },
            sx: {
              '& .MuiList-root': {
                padding: '6px',
                borderRadius: '8px',
                fontSize: '14px',
              },
              '& .MuiPopver-paper': {
                boxShadow: `0px 8px 12px ${alpha(
                  theme.palette.other.lightBrandBlueLogin,
                  0.31
                )}`,
                marginTop: '5px',
              },
              '& .MuiMenu-paper': {
                backgroundColor: theme.palette.other.menuBackground,
                boxShadow: `0px 8px 12px ${alpha(
                  theme.palette.other.lightBrandBlueLogin,
                  0.31
                )}`,
                marginTop: '5px',
              },
              '& .MuiMenuItem-root': {
                color: alpha(theme.palette.primary.main, 0.5),
                backgroundColor: theme.palette.other.menuItemColor,
                marginTop: '4px',
                height: '40px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: theme.palette.other.blue1,
                },
                '&:active': {
                  color: theme.palette.other.black,
                  backgroundColor: theme.palette.secondary.main,
                  '&&&:before': {
                    backgroundColor: theme.palette.secondary.main,
                  },
                  '&&:after': {
                    backgroundColor: theme.palette.secondary.main,
                  },
                },
                '&:focus': {
                  backgroundColor: theme.palette.other.blue1,
                },
              },
            },
            // getContentAnchorEl: null,
          }}
          {...props}
          ref={selectRef}
          onOpen={handleOpen}
          onClose={handleClose}
          variant="outlined"
          sx={{
            '& .MuiFilledInput-input': {
              lineHeight: '19.07px',
              fontWeight: '500 !important',
              fontStyle: 'normal',
            },

            '& .MuiSelect-select .notranslate::after':
              placeholder && labelShrinkState === 'true'
                ? {
                    content: `"${placeholder}"`,
                    opacity: 0.42,
                  }
                : {},
            ...props?.sx,
          }}
        >
          {children}
        </Select>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
    );
  }
);

DropDown.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  helperText: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  required: PropTypes.bool.isRequired,
  placeholder: PropTypes.string.isRequired,
  labelSx: PropTypes.shape({}).isRequired,
  sx: PropTypes.shape({}).isRequired,
};

const DropdownItem = memo(({ children, ...props }) => (
  <MenuItem
    {...props}
    sx={(theme) => ({
      '&.Mui-disabled': {
        backgroundColor: theme.palette.other.white,
        WebkitTextFillColor: theme.palette.text.disabled,
        opacity: 1,
      },
    })}
  >
    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      <Typography variant="body2">{children}</Typography>
    </div>
  </MenuItem>
));

DropdownItem.propTypes = {
  children: PropTypes.node.isRequired,
};

export { DropDown, DropdownItem };
